import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import LocationDropdown from '../../tabs/Dropdown/LocationName';
import { format, parseISO } from 'date-fns';

const HospitalNewCases = () => {
  // Sidebar and UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Modal & editing states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);

  // Data
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [page, setPage] = useState(1);

  // Filters
  const [filterForm, setFilterForm] = useState({ case_name: '', location_id: '' });

  // Role and user info
  const [roleId, setRoleId] = useState(null);
  const UserId = sessionStorage.getItem('user_id');

  // Date and time format
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, settingsRef, updateSetting, handleColorTheme } = useSettings();
  const limit = 10; // pagination per page

  // Sidebar toggle
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Utility alert
  const showAlert = (message, isSuccess = true) =>
    alert(`${isSuccess ? '✅ Success:' : '❌ Error:'} ${message}`);

  // Format date & time safely
  const formatCaseDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
    const timeFormat = sessionStorage.getItem('time_format') || '24';

    // Map WordPress-style formats to date-fns syntax
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    // ✅ Parse both ISO and non-ISO timestamps as *UTC*, not local time
    let utcDate;
    if (dateString.includes('T')) {
      // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
      utcDate = new Date(dateString);
    } else {
      // MySQL-like (e.g. 2025-10-27 16:35:40)
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
    }

    // Format without applying local timezone
    const utcTimestamp = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds()
    );

    return format(utcTimestamp, formatString);
  } catch (err) {
    console.error('Error formatting date:', err, dateString);
    return 'Invalid Date';
  }
};

  // Load role ID and handle query params
  useEffect(() => {
  const storedRoleId = sessionStorage.getItem('Role_id');
  if (storedRoleId) {
    setRoleId(storedRoleId);
  } else {
    console.warn('Role_id not found in sessionStorage');
    // Optionally fetch roleId from an auth endpoint if needed
  }
}, []);

  // Fetch cases when role ID is ready
  useEffect(() => {
    if (roleId) fetchCases();
  }, [roleId]);

  // Filter cases dynamically
  useEffect(() => {
  const filtered = cases.filter((c) => {
    const nameMatch = filterForm.case_name
      ? c.case_name?.toLowerCase().includes(filterForm.case_name.toLowerCase())
      : true;
    const locationMatch = filterForm.location_id
      ? c.case_location === parseInt(filterForm.location_id)
      : true;
    console.log('Filtering:', { nameMatch, locationMatch, case: c }); // Debug
    return nameMatch && locationMatch;
  });
  setFilteredCases(filtered);
}, [cases, filterForm]);

  // Fetch new hospital cases
const fetchCases = async () => {
  setIsLoading(true);

  try {
    const response = await axiosInstance.get(
      `/ems/records/${roleId}/hospital_new_cases/${UserId}`
    );

    const data = response?.data;

    let cases = [];

    if (Array.isArray(data)) {
      cases = data;
    }

    else if (Array.isArray(data?.newcase)) {
      cases = data.newcase;
    }

    else if (Array.isArray(data?.data)) {
      cases = data.data;
    }

    else if (Array.isArray(data?.records)) {
      cases = data.records;
    }

    else {
      console.warn("Unexpected API format:", data);
      cases = [];
    }

    setCases(cases);
  } catch (error) {
    console.error("Error fetching cases:", error);
    showAlert(error.response?.data?.message || "Failed to fetch cases", false);
    setCases([]);
  } finally {
    setIsLoading(false);
  }
};




// Filter cases dynamically
useEffect(() => {
  if (!Array.isArray(cases)) {
    console.warn("cases is not an array:", cases);
    setFilteredCases([]);
    return;
  }

  const filtered = cases.filter((c) => {
    const nameMatch = filterForm.case_name
      ? c.case_name?.toLowerCase().includes(filterForm.case_name.toLowerCase())
      : true;

    const locationMatch = filterForm.location_id
      ? c.case_location === parseInt(filterForm.location_id)
      : true;

    return nameMatch && locationMatch;
  });

  setFilteredCases(filtered);
}, [cases, filterForm]);


  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => setFilterForm({ case_name: '', location_id: '' });

  // View case details
  const handleViewDetails = (caseId) => {
    sessionStorage.setItem('currentCaseId', caseId);
    navigate('/ems/hospital-detail');
  };

  // Pagination
  const total = filteredCases.length;
  const currentItems = filteredCases.slice((page - 1) * limit, page * limit);

  return (
    <div id="main-wrapper">
      {/* Sidebar */}
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        {/* Topbar */}
        <TopBar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            {/* Settings */}
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            {/* Main Card */}
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Hospital New Cases</h5>
                <p className="card-subtitle mb-0 mt-1">
                  View and track all new hospital cases
                </p>
              </div>

              <div className="card-body p-4">
                {/* Filter Section */}
                <div className="filter-search mt-4">
                  <h6 className="px-2 mb-0">Filter</h6>
                  <div className="filter-panel">
                    <form className="row">
                      <div className="form-group col-md-4 p-2">
                        <input
                          type="text"
                          className="form-control"
                          name="case_name"
                          placeholder="Case Name"
                          value={filterForm.case_name}
                          onChange={handleFilterChange}
                        />
                      </div>

                      <div className="form-group col-md-4 p-2">
                        <LocationDropdown
                          value={filterForm.location_id}
                          onChange={handleFilterChange}
                          name="location_id"
                        />
                      </div>

                      <div className="form-group col-md-3 p-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-rounded"
                          onClick={clearFilters}
                        >
                          Clear Filter
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Cases Table */}
                <div className="py-3">
                  <div className="table-responsive rounded-2 ros-table">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th><h6 className="fw-semibold mb-0">ID</h6></th>
                          <th><h6 className="fw-semibold mb-0">Name</h6></th>
                          <th><h6 className="fw-semibold mb-0">Location</h6></th>
                          <th><h6 className="fw-semibold mb-0">Ambulance</h6></th>
                          <th><h6 className="fw-semibold mb-0">Medical Center</h6></th>
                          <th><h6 className="fw-semibold mb-0">Case Date</h6></th>
                          <th><h6 className="fw-semibold mb-0">Action</h6></th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : filteredCases.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No Cases Found
                            </td>
                          </tr>
                        ) : (
                          currentItems.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  className="text-primary border-0 bg-transparent p-0"
                                  onClick={() => handleViewDetails(item.id)}
                                >
                                  {item.id}
                                </button>
                              </td>
                              <td>
                                <button
                                  className="text-primary border-0 bg-transparent p-0"
                                  onClick={() => handleViewDetails(item.id)}
                                >
                                  {item.case_name}
                                </button>
                              </td>
                              <td>{item.location?.location_name || 'N/A'}</td>
                              <td>{item.ambulance?.plate_number || 'N/A'}</td>
                              <td>{item.medical_center?.name || 'N/A'}</td>
                              <td>{formatCaseDate(item.created_at)}</td>
                              <td>
                                <button
                                  className="h4 border-0 bg-transparent"
                                  onClick={() => handleViewDetails(item.id)}
                                  aria-label="View details"
                                >
                                  <i className="ti ti-list-details text-success"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {filteredCases.length > 0 && (
                  <Pagination
                    total={total}
                    limit={limit}
                    page={page}
                    onPageChange={setPage}
                    disabled={isLoading}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalNewCases;
